/**
 * Views — DESIGN.md §7.6, §13.1.
 *
 * "Views live in the project sidebar (project-scoped) or the global 'Views'
 * nav item (cross-project)."
 *
 * This is the cross-project list: every shared view, plus the viewer's own,
 * with the actions §7.6 names — open, duplicate, publish, delete.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarDays, Columns3, LayoutGrid, List, Share2, Table2, Trash2 } from 'lucide-react';
import type { View, ViewType } from '@slate/shared';
import { Page } from '@/components/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { UserAvatar } from '@/components/ui/avatar';
import { useViewMutations, useViews } from '@/features/views/api';
import { useProjects } from '@/features/projects/api';
import { relativeTime } from '@/lib/utils';

export const Route = createFileRoute('/views')({
  component: ViewsPage,
});

const TYPE_ICONS: Record<ViewType, typeof List> = {
  list: List,
  // #80 — a saved table view is listed like any other; the icon is what tells
  // it from the list view it shares its columns with.
  table: Table2,
  kanban: Columns3,
  calendar: CalendarDays,
};

function ViewsPage(): JSX.Element {
  const { data: views = [], isLoading } = useViews();
  const { data: projects } = useProjects('all');
  const mutations = useViewMutations();
  const navigate = useNavigate();

  const projectKey = (id: string | null): string | null =>
    id ? (projects?.items.find((project) => project.id === id)?.key ?? null) : null;

  const open = (view: View): void => {
    const key = projectKey(view.projectId);
    if (key) {
      void navigate({ to: '/projects/$key', params: { key }, search: { view: view.id } });
      return;
    }
    // A cross-project view has no project page to open on; M3 renders it
    // through the same surfaces, so it lands on the first project until a
    // dedicated `/views/:id` screen exists.
    const first = projects?.items[0];
    if (first) {
      void navigate({
        to: '/projects/$key',
        params: { key: first.key },
        search: { view: view.id },
      });
    }
  };

  if (isLoading) return <LoadingRegion label="Loading views" />;

  const shared = views.filter((view) => view.scope === 'shared');
  const personal = views.filter((view) => view.scope === 'personal');

  return (
    <Page
      title="Views"
      description="Saved filters, sorts and layouts. Shared views are visible to everyone."
    >
      {views.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid className="size-8" />}
          title="No saved views yet"
          description="Adjust a project's filters, sort or layout, then use “Save as…” to keep it. Publishing makes it available to the whole team."
        />
      ) : (
        <div className="space-y-6">
          <Section
            title="Shared"
            hint="Everyone sees these. The owner and administrators can edit them."
            views={shared}
            projectKey={projectKey}
            onOpen={open}
            mutations={mutations}
          />
          <Section
            title="My views"
            hint="Only you see these."
            views={personal}
            projectKey={projectKey}
            onOpen={open}
            mutations={mutations}
          />
        </div>
      )}
    </Page>
  );
}

function Section({
  title,
  hint,
  views,
  projectKey,
  onOpen,
  mutations,
}: {
  title: string;
  hint: string;
  views: View[];
  projectKey: (id: string | null) => string | null;
  onOpen: (view: View) => void;
  mutations: ReturnType<typeof useViewMutations>;
}): JSX.Element | null {
  if (views.length === 0) return null;

  return (
    <section>
      <h2 className="text-sm font-semibold">{title}</h2>
      <p className="text-muted-foreground mb-2 text-xs">{hint}</p>
      <ul className="divide-y rounded-lg border">
        {views.map((view) => {
          const Icon = TYPE_ICONS[view.type];
          const key = projectKey(view.projectId);
          return (
            <li key={view.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                onClick={() => onOpen(view)}
              >
                {view.name}
              </button>
              {key ? <Badge variant="secondary">{key}</Badge> : <Badge>Cross-project</Badge>}
              {view.isProjectDefault ? <Badge variant="success">Project default</Badge> : null}
              <span className="text-muted-foreground hidden items-center gap-1 text-xs sm:flex">
                <UserAvatar user={view.owner} className="size-5" />
                {view.owner.name} · {relativeTime(view.updatedAt)}
              </span>

              <div className="flex shrink-0 gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => mutations.duplicate.mutate(view.id)}
                >
                  Duplicate
                </Button>
                {view.canEdit ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={
                        view.scope === 'shared' ? `Unpublish ${view.name}` : `Publish ${view.name}`
                      }
                      onClick={() =>
                        mutations.share.mutate({ id: view.id, shared: view.scope !== 'shared' })
                      }
                    >
                      <Share2 className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={`Delete ${view.name}`}
                      onClick={() => {
                        const message =
                          view.scope === 'shared'
                            ? `Delete “${view.name}”? It is a SHARED view — it will disappear for everyone.`
                            : `Delete “${view.name}”?`;
                        if (window.confirm(message)) mutations.remove.mutate(view.id);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
